/**
 * Patient Edit Dialog with CNP validation
 */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { parseCNP, validateCNP } from './utils';
import { formatName, formatPhone, validatePhone } from '../../utils/formatting';
import { AlertCircle } from 'lucide-react';
import axios from 'axios';
import { DatePicker } from '../ui/date-picker';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PatientEditDialog = ({ 
  open, 
  onOpenChange, 
  formData: externalFormData, 
  setFormData: externalSetFormData, 
  onSubmit,
  patient, // Alternative: pass patient directly
  onSave, // Alternative: callback with (patientId, data)
  currentPatientId // ID of the patient being edited (to exclude from duplicate check)
}) => {
  const [cnpError, setCnpError] = useState(null);
  const [phoneError, setPhoneError] = useState(null);
  const [emergencyPhoneError, setEmergencyPhoneError] = useState(null);
  const [isDuplicateCNP, setIsDuplicateCNP] = useState(false);
  const [isCheckingCNP, setIsCheckingCNP] = useState(false);
  
  // Internal form data state (used when patient prop is provided)
  const [internalFormData, setInternalFormData] = useState({});
  
  // Determine which mode we're in
  const isStandaloneMode = !!patient;
  const formData = isStandaloneMode ? internalFormData : externalFormData;
  const setFormData = isStandaloneMode ? setInternalFormData : externalSetFormData;
  const patientId = isStandaloneMode ? patient?.id : currentPatientId;

  // Initialize form data when patient changes (standalone mode)
  useEffect(() => {
    if (patient && open) {
      setInternalFormData({
        first_name: patient.first_name || '',
        last_name: patient.last_name || '',
        cnp: patient.cnp || '',
        date_of_birth: patient.date_of_birth || '',
        gender: patient.gender || 'M',
        height: patient.height || '',
        phone: patient.phone || '',
        email: patient.email || '',
        address: patient.address || '',
        emergency_contact: patient.emergency_contact || '',
        emergency_phone: patient.emergency_phone || '',
        blood_type: patient.blood_type || '',
        allergies: patient.allergies || '',
        notes: patient.notes || ''
      });
    }
  }, [patient, open]);

  // Reset errors when dialog closes
  useEffect(() => {
    if (!open) {
      setCnpError(null);
      setPhoneError(null);
      setEmergencyPhoneError(null);
      setIsDuplicateCNP(false);
    }
  }, [open]);

  // Check for duplicate CNP
  const checkDuplicateCNP = async (cnp) => {
    if (!cnp || cnp.length !== 13) return;
    
    setIsCheckingCNP(true);
    try {
      const response = await axios.get(`${API_URL}/patients`);
      const patients = response.data?.patients || response.data || [];
      const duplicate = patients.find(p => p.cnp === cnp && p.id !== patientId);
      
      if (duplicate) {
        setIsDuplicateCNP(true);
        setCnpError('Acest CNP există deja');
      } else {
        setIsDuplicateCNP(false);
        // Only clear error if it was a duplicate error, keep validation errors
        if (cnpError === 'Acest CNP există deja') {
          setCnpError(null);
        }
      }
    } catch (error) {
      console.error('Error checking CNP:', error);
    } finally {
      setIsCheckingCNP(false);
    }
  };

  const handleCNPChange = (cnp) => {
    // Only allow digits
    const cleanCNP = cnp.replace(/\D/g, '');
    setFormData(prev => ({ ...prev, cnp: cleanCNP }));
    
    // Reset errors
    setCnpError(null);
    setIsDuplicateCNP(false);
    
    // Validate CNP format
    if (cleanCNP.length === 13) {
      const validation = validateCNP(cleanCNP);
      if (!validation.valid) {
        setCnpError(validation.error);
      } else {
        // CNP is valid, check for duplicates
        checkDuplicateCNP(cleanCNP);
      }
    } else if (cleanCNP.length > 0 && cleanCNP.length < 13) {
      setCnpError('CNP-ul trebuie să aibă 13 cifre');
    }
    
    // Parse CNP for auto-fill
    if (cleanCNP.length >= 7) {
      const parsed = parseCNP(cleanCNP);
      if (parsed) {
        setFormData(prev => ({
          ...prev,
          cnp: cleanCNP,
          date_of_birth: parsed.dateOfBirth,
          gender: parsed.gender
        }));
      }
    }
  };

  // Handle phone input with validation
  const handlePhoneChange = (value) => {
    const formatted = formatPhone(value);
    setFormData(prev => ({ ...prev, phone: formatted }));
    
    // Validate on change
    if (formatted.length > 0) {
      const validation = validatePhone(formatted);
      setPhoneError(validation.error);
    } else {
      setPhoneError(null);
    }
  };

  // Handle emergency phone input with validation
  const handleEmergencyPhoneChange = (value) => {
    const formatted = formatPhone(value);
    setFormData(prev => ({ ...prev, emergency_contact_phone: formatted, emergency_phone: formatted }));
    
    // Validate on change
    if (formatted.length > 0) {
      const validation = validatePhone(formatted);
      setEmergencyPhoneError(validation.error);
    } else {
      setEmergencyPhoneError(null);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Final validation before submit
    if (formData.cnp) {
      const validation = validateCNP(formData.cnp);
      if (!validation.valid) {
        setCnpError(validation.error);
        return;
      }
      if (isDuplicateCNP) {
        setCnpError('Acest CNP există deja');
        return;
      }
    }

    // Validate phone
    if (formData.phone) {
      const phoneValidation = validatePhone(formData.phone);
      if (!phoneValidation.valid) {
        setPhoneError(phoneValidation.error);
        return;
      }
    }

    // Validate emergency phone
    const emergencyPhone = formData.emergency_contact_phone || formData.emergency_phone;
    if (emergencyPhone) {
      const emergencyPhoneValidation = validatePhone(emergencyPhone);
      if (!emergencyPhoneValidation.valid) {
        setEmergencyPhoneError(emergencyPhoneValidation.error);
        return;
      }
    }
    
    // Handle standalone mode (with patient prop)
    if (isStandaloneMode && onSave) {
      const cleanedData = {
        ...formData,
        height: formData.height === '' ? null : parseFloat(formData.height),
        phone: formData.phone || null,
        email: formData.email || null,
        address: formData.address || null,
        emergency_contact: formData.emergency_contact || null,
        emergency_phone: formData.emergency_contact_phone || formData.emergency_phone || null,
        blood_type: formData.blood_type || null,
        allergies: formData.allergies || null,
        notes: formData.notes || null,
      };
      onSave(patientId, cleanedData);
    } else {
      onSubmit(e);
    }
  };

  const hasError = cnpError || isDuplicateCNP || phoneError || emergencyPhoneError;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editează Pacient</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit-cnp">CNP *</Label>
              <Input
                id="edit-cnp"
                value={formData.cnp || ''}
                onChange={(e) => handleCNPChange(e.target.value)}
                maxLength={13}
                required
                className={hasError ? 'border-red-500 focus-visible:ring-red-500' : ''}
                data-testid="edit-patient-cnp"
              />
              {hasError && (
                <div className="flex items-center gap-1.5 text-red-600 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{cnpError}</span>
                </div>
              )}
              {isCheckingCNP && (
                <p className="text-xs text-slate-500">Verificare CNP...</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-last_name">Nume *</Label>
              <Input
                id="edit-last_name"
                value={formData.last_name || ''}
                onChange={(e) => setFormData({ ...formData, last_name: formatName(e.target.value) })}
                required
                placeholder="ex: Popescu sau Ionescu-Popa"
                data-testid="edit-patient-lastname"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-first_name">Prenume *</Label>
              <Input
                id="edit-first_name"
                value={formData.first_name || ''}
                onChange={(e) => setFormData({ ...formData, first_name: formatName(e.target.value) })}
                required
                placeholder="ex: Ion sau Ana-Maria"
                data-testid="edit-patient-firstname"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-dob">Data nașterii</Label>
              <DatePicker
                value={formData.date_of_birth || ''}
                onChange={(date) => setFormData({ ...formData, date_of_birth: date })}
                placeholder="Se completează din CNP"
                disabled={true}
                data-testid="edit-patient-dob"
              />
            </div>
            <div className="space-y-2">
              <Label>Gen</Label>
              <Select 
                value={formData.gender || 'M'} 
                onValueChange={(v) => setFormData({ ...formData, gender: v })}
              >
                <SelectTrigger data-testid="edit-patient-gender">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="M">Masculin</SelectItem>
                  <SelectItem value="F">Feminin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-height">Înălțime (cm)</Label>
              <Input
                id="edit-height"
                type="number"
                value={formData.height || ''}
                onChange={(e) => setFormData({ ...formData, height: e.target.value ? parseFloat(e.target.value) : null })}
                placeholder="175"
                data-testid="edit-patient-height"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-phone">Telefon</Label>
              <Input
                id="edit-phone"
                value={formData.phone || ''}
                onChange={(e) => handlePhoneChange(e.target.value)}
                placeholder="07xxxxxxxx"
                maxLength={10}
                className={phoneError ? 'border-red-500 focus-visible:ring-red-500' : ''}
                data-testid="edit-patient-phone"
              />
              {phoneError && (
                <div className="flex items-center gap-1.5 text-red-600 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{phoneError}</span>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={formData.email || ''}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                data-testid="edit-patient-email"
              />
            </div>
            <div className="space-y-2">
              <Label>Grupa sanguină</Label>
              <Select 
                value={formData.blood_type || ''} 
                onValueChange={(v) => setFormData({ ...formData, blood_type: v })}
              >
                <SelectTrigger data-testid="edit-patient-bloodtype">
                  <SelectValue placeholder="Selectați" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="A+">A+</SelectItem>
                  <SelectItem value="A-">A-</SelectItem>
                  <SelectItem value="B+">B+</SelectItem>
                  <SelectItem value="B-">B-</SelectItem>
                  <SelectItem value="AB+">AB+</SelectItem>
                  <SelectItem value="AB-">AB-</SelectItem>
                  <SelectItem value="O+">O+</SelectItem>
                  <SelectItem value="O-">O-</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-address">Adresă</Label>
            <Input
              id="edit-address"
              value={formData.address || ''}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              data-testid="edit-patient-address"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit-emergency">Contact urgență</Label>
              <Input
                id="edit-emergency"
                value={formData.emergency_contact_name || formData.emergency_contact || ''}
                onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })}
                data-testid="edit-patient-emergency-contact"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-emergency-phone">Telefon urgență</Label>
              <Input
                id="edit-emergency-phone"
                value={formData.emergency_contact_phone || formData.emergency_phone || ''}
                onChange={(e) => handleEmergencyPhoneChange(e.target.value)}
                placeholder="07xxxxxxxx"
                maxLength={10}
                className={emergencyPhoneError ? 'border-red-500 focus-visible:ring-red-500' : ''}
                data-testid="edit-patient-emergency-phone"
              />
              {emergencyPhoneError && (
                <div className="flex items-center gap-1.5 text-red-600 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{emergencyPhoneError}</span>
                </div>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-allergies">Alergii</Label>
            <Textarea
              id="edit-allergies"
              value={formData.allergies || ''}
              onChange={(e) => setFormData({ ...formData, allergies: e.target.value })}
              rows={2}
              data-testid="edit-patient-allergies"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-notes">Observații</Label>
            <Textarea
              id="edit-notes"
              value={formData.notes || ''}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              data-testid="edit-patient-notes"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
            >
              Anulează
            </Button>
            <Button 
              type="submit" 
              disabled={hasError || isCheckingCNP}
              data-testid="save-patient-btn"
            >
              Salvează
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
